import React, {useState, useEffect, useCallback} from 'react';
import ReactPaginate from 'react-paginate';
import { useSearchParams } from 'react-router-dom';
import axios, { getErrorMessage } from "../../api/axios";

// Components
import CarCard from './CarCard';
import NewCarForm from './NewCarForm';
import AddToCollectionModal from './AddToCollectionModal'; // ADDED
import CsvImportModal from './CsvImportModal'; // ADDED

export default function Models(){

    // MODIFIED: the five filter arrays are now filled from the server instead of staying empty,
    // and the chosen value of each one is tracked so it can be sent with the query.
    const [filterOptions, setFilterOptions] = useState({ series: [], brands: [], models: [], years: [] });
    const [selectedFilters, setSelectedFilters] = useState({ series: 'all', brand: 'all', model: 'all', year: 'all' });
    const [sort, setSort] = useState('AZ'); // ADDED: the sort dropdown was never wired up

    // ADDED: the landing page can arrive here with ?search=...
    const [searchParams] = useSearchParams();
    const [searchModelInput, setSearchModelInput] = useState(searchParams.get('search') || '');
    // Debounced copy of the search box, so we do not fire a request on every keystroke
    const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || '');

    // Errror handling
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true); // ADDED

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);  
    const [totalPages, setTotalPages] = useState(1);    
    const itemsPerPage = 25;

    // Models' data
    const [models, setModels] = useState([]);
    // ADDED: current eBay asking prices, keyed by car id. Loaded after the grid so a slow
    // eBay never delays the cards, and left empty when eBay is not configured.
    const [markets, setMarkets] = useState({});

    // Other components
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [isImportModalVisible, setIsImportModalVisible] = useState(false); // ADDED
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [selectedCarId, setSelectedCarId] = useState(null);  // ADDED: which car the View modal shows
    const [carToAdd, setCarToAdd] = useState(null);            // ADDED: which car the Add modal is for
    // ADDED: filters are hidden behind a toggle on phones so the grid is not pushed off-screen
    const [showFilters, setShowFilters] = useState(false);

    // ADDED: wait 400ms after the last keystroke before searching
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchModelInput);
            setCurrentPage(1); // a new search always starts at page 1
        }, 400);
        return () => clearTimeout(timer);
    }, [searchModelInput]);

    // MODIFIED: the filter values are reloaded whenever the Make changes, so the Models
    // dropdown only ever lists castings that make actually has. It is also called after a
    // model is created, so a brand new make or casting appears in the dropdowns without a
    // page reload.
    const loadFilters = useCallback(async () => {
        try {
            const response = await axios.get('/cars/filters', {
                params: { brand: selectedFilters.brand },
            });
            setFilterOptions(response.data);
        } catch (err) {
            console.error(err); // a failed filter load should not block the grid
        }
    }, [selectedFilters.brand]);

    useEffect(() => { loadFilters(); }, [loadFilters]);

    // MODIFIED: hits /cars (which now exists) and sends the filters and sort along
    const fetchModels = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await axios.get('/cars', {
                params: {
                    page: currentPage,
                    limit: itemsPerPage,
                    search: debouncedSearch,
                    series: selectedFilters.series,
                    brand: selectedFilters.brand,
                    model: selectedFilters.model,
                    year: selectedFilters.year,
                    sort,
                }
            });

            setModels(response.data.models);
            setTotalPages(response.data.totalPages);
            setError(null);
        } catch (err) {
            setError(new Error(getErrorMessage(err, 'Could not load the catalog.')));
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, debouncedSearch, selectedFilters, sort]);

    useEffect(() => { fetchModels(); }, [fetchModels]);

    // ADDED: one batched market-value request for the 25 cards on the page, sent once the
    // cards are already on screen. Failures are swallowed on purpose - the card then just
    // keeps showing the catalog price.
    useEffect(() => {
        if (models.length === 0) return;
        let isCurrent = true;

        axios.post('/cars/market', { ids: models.map((model) => model.id) })
            .then((response) => { if (isCurrent) setMarkets(response.data.markets || {}); })
            .catch(() => { if (isCurrent) setMarkets({}); });

        return () => { isCurrent = false; };
    }, [models]);

    const handlePageClick = (data) => {
        setCurrentPage(data.selected + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' }); // ADDED: paging on a phone should go back to the top
    };

    // ADDED: one handler for all four dropdowns
    const handleFilterChange = (key, value) => {
        setSelectedFilters((previous) => ({
            ...previous,
            [key]: value,
            // MODIFIED: a model belongs to one make, so switching make clears a chosen
            // model - otherwise the grid would filter on a pairing that cannot exist and
            // come back empty with no obvious reason why.
            ...(key === 'brand' ? { model: 'all' } : {}),
        }));
        setCurrentPage(1);
    };

    const handleCreateButtonClick = () => {
        setIsCreateModalVisible(!isCreateModalVisible);
    };

    // MODIFIED: the View button now knows which car it is showing
    const handleViewModalClick = (carId = null) => {
        setSelectedCarId(carId);
        setIsViewModalVisible(!isViewModalVisible);
    };

    // The four filter dropdowns, described once and rendered in a loop
    // MODIFIED: filter ids are prefixed so they cannot collide with the create
    // form's inputs, which reuse names like "model", "series" and "theme".
    const filterFields = [
        { key: 'series', label: 'Series', options: filterOptions.series },
        { key: 'brand', label: 'Makes', options: filterOptions.brands },
        { key: 'model', label: 'Models', options: filterOptions.models },
        { key: 'year', label: 'Year', options: filterOptions.years },
    ];

    // MODIFIED (styling): the panel sat inside a second bordered box with p-10 padding.
    // It is now a single card that uses the theme tokens.
    const filterPanel = (
        <div className='px-4 pb-4 pt-2 lg:pt-4'>
            <div className='hidden lg:flex flex-row items-center gap-2 pb-3'>
                <h2 className='cv-display text-lg'>Filters</h2>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-brand-flame">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
                </svg>
            </div>
            <div className='flex flex-col space-y-3'>
                {filterFields.map((field) => (
                    <div key={field.key}>
                        <label htmlFor={`filter-${field.key}`} className="cv-label">{field.label}</label>
                        <select
                            name={field.key}
                            id={`filter-${field.key}`}
                            value={selectedFilters[field.key]}
                            onChange={(e) => handleFilterChange(field.key, e.target.value)}
                            className="cv-input">
                            <option value="all">All</option>
                            {field.options.map((option) => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>
                ))}
                {/* ADDED: a way back to an unfiltered list */}
                <button
                    onClick={() => { setSelectedFilters({ series: 'all', brand: 'all', model: 'all', year: 'all' }); setCurrentPage(1); }}
                    className="text-sm cv-accent underline pt-1">
                    Clear filters
                </button>
            </div>
        </div>
    );

    return(
        <>
        {/* MODIFIED (styling): the page header now matches the Collections and Account
            pages - eyebrow, display heading, one line of copy. */}
        <section className='cv-page pt-8 pb-6 px-4 sm:px-8'>
            <div className='max-w-6xl mx-auto text-center space-y-2'>
                <p className='cv-eyebrow'>The catalog</p>
                <h1 className='cv-display text-4xl sm:text-5xl'>Models</h1>
                <p className='text-sm cv-muted max-w-xl mx-auto'>
                    Every
                    <img src='img/hotwheels-logo.png' alt="Hot Wheels" className="w-14 h-4 inline mx-1.5 align-middle"/>
                    model in the shared catalog. Add the ones you own to a collection.
                </p>
            </div>
        </section>

        {/* MODIFIED: one column on phones, sidebar + grid from lg up */}
        <section className="cv-page grid grid-cols-1 lg:grid-cols-4 gap-5 px-4 sm:px-8 pb-8 max-w-6xl mx-auto w-full">
            {/* Filter sidebar: a collapsible panel below lg, always open from lg up */}
            <div className="cv-card lg:col-span-1 h-fit overflow-hidden lg:sticky lg:top-24">
                <button onClick={() => setShowFilters(!showFilters)}
                    aria-expanded={showFilters}
                    className="lg:hidden w-full flex justify-between items-center px-4 py-3 cv-accent font-display font-semibold uppercase tracking-wider text-sm">
                    <span>Filters</span>
                    <span className="text-lg leading-none">{showFilters ? '\u2212' : '+'}</span>
                </button>
                <div className={showFilters ? 'block' : 'hidden lg:block'}>
                    {filterPanel}
                </div>
            </div>

            <div className="lg:col-span-3 space-y-5">
                {/* MODIFIED: the toolbar wrapped badly on phones; it now stacks */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="relative w-full">
                        <input
                            id="searchModelInput"
                            value={searchModelInput}
                            onChange={(e) => setSearchModelInput(e.target.value)}
                            type="text"
                            autoComplete="off"
                            placeholder="Search for a model"
                            className="cv-input rounded-full pl-11"
                        />
                        <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 cv-muted">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                            </svg>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button className='cv-btn cv-btn-outline whitespace-nowrap'
                                onClick={handleCreateButtonClick}>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                            </svg>
                            Create
                        </button>
                        {/* ADDED: bulk add from a spreadsheet */}
                        <button className='cv-btn cv-btn-quiet whitespace-nowrap'
                                onClick={() => setIsImportModalVisible(true)}
                                title="Import models from a CSV file">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                            </svg>
                            <span className="hidden sm:inline">Import</span>
                        </button>
                        {/* MODIFIED: bound to state and refetches on change */}
                        <select value={sort} onChange={(e) => { setSort(e.target.value); setCurrentPage(1); }}
                                aria-label="Sort models"
                                className='cv-input w-auto py-2.5'>
                            <option value="AZ">A-Z</option>
                            <option value="ZA">Z-A</option>
                            <option value="newest">Newest</option>
                            <option value="oldest">Oldest</option>
                            <option value="price">Price</option>
                        </select>
                    </div>
                </div>

                {error && <p className='cv-alert-error text-center'>{error.message}</p>}
                {isLoading && <p className='text-center cv-muted py-10'>Loading models...</p>}
                {/* ADDED: an empty catalog used to render as a blank page with no explanation */}
                {!isLoading && !error && models.length === 0 &&
                    <div className="cv-card border-dashed p-8 text-center space-y-2">
                        <p className="cv-strong">No models match what you are looking for.</p>
                        <p className="text-sm cv-muted">Try clearing the filters, or use Create to add a model to the catalog.</p>
                    </div>
                }

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {models.map((model) => (
                        <div key={model.id} className='cv-card cv-card-interactive cv-speedline flex flex-col'>
                            <div className='p-4 flex-1'>
                                {/* MODIFIED: fixed height box with a fallback, so cards line up
                                    even when a model has no photo */}
                                <div className="relative">
                                    <div className="cv-well w-full h-40 rounded-xl flex items-center justify-center overflow-hidden">
                                        {/* MODIFIED: the ~20KB preview. Lists no longer receive the full photo, which */}
                                        {/* pushed a page over the database's 5MB response limit. */}
                                        {model.imageThumb
                                            ? <img src={model.imageThumb} alt={model.name} className="w-full h-full object-contain"/>
                                            : <span className="text-xs cv-muted">No image</span>}
                                    </div>
                                    {/* ADDED: how many of this casting you own, summed across all your
                                        collections. Hidden entirely when you own none. */}
                                    {model.ownedQuantity > 0 &&
                                        <span
                                            title={`You own ${model.ownedQuantity} of this model`}
                                            className="absolute top-2 right-2 bg-brand-flame text-white font-display font-bold text-xs px-2 py-1 rounded-lg shadow">
                                            <span aria-hidden="true">&times;{model.ownedQuantity}</span>
                                            <span className="sr-only">You own {model.ownedQuantity}</span>
                                        </span>
                                    }
                                </div>
                                {/* MODIFIED: was model.description, a field that does not exist */}
                                <p className='text-[0.65rem] cv-accent uppercase tracking-widest mt-3 truncate'>{model.hwcollectionName}</p>
                                <h2 className='cv-title text-lg mt-1 break-words leading-snug'>{model.name}</h2>
                                <p className='text-xs cv-muted mt-1'>{model.brand} {model.model} · {model.datereleased}</p>
                                {/* MODIFIED: shows what the casting is currently ASKING on eBay when
                                    that is known, and the catalog price otherwise. These are live
                                    asking prices from active listings, not realised sale prices, so
                                    the label says so. Collection totals still use the catalog price. */}
                                {markets[model.id]
                                    ? <div className='mt-2'>
                                        <p className='font-display font-bold cv-strong'>
                                            ${Number(markets[model.id].average).toFixed(2)}
                                            <span className='text-[0.65rem] cv-muted font-sans font-normal ml-1.5 uppercase tracking-wider'>avg asking</span>
                                        </p>
                                        <p className='text-[0.65rem] cv-muted'>
                                            ${Number(markets[model.id].low).toFixed(2)} &ndash; ${Number(markets[model.id].high).toFixed(2)} on eBay ({markets[model.id].listingCount})
                                        </p>
                                      </div>
                                    /* MODIFIED: price is optional now - an unpriced model shows a
                                       dash, so it is not mistaken for one worth nothing. */
                                    : <p className='font-display font-bold cv-strong mt-2'>
                                        {model.price === null || model.price === undefined
                                            ? <span className='cv-muted font-normal text-sm'>Not priced</span>
                                            : `$${Number(model.price).toFixed(2)}`}
                                      </p>}
                            </div>
                            {/* MODIFIED: two buttons that do something, rather than three that did not. */}
                            <div className='flex flex-row' style={{ borderTop: '1px solid var(--border-subtle)' }}>
                                <button onClick={() => handleViewModalClick(model.id)}
                                    className='w-1/2 py-2.5 cv-muted hover:text-brand-flame transition duration-300 flex items-center justify-center gap-1.5 text-sm'>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                        <path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                                        <path fillRule="evenodd" d="M.664 10.59a1.651 1.651 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41ZM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" clipRule="evenodd" />
                                    </svg>
                                    View
                                </button>
                                <button onClick={() => setCarToAdd(model)}
                                    className='w-1/2 py-2.5 cv-accent hover:text-brand-crimson transition duration-300 flex items-center justify-center gap-1.5 text-sm font-semibold'
                                    style={{ borderLeft: '1px solid var(--border-subtle)' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-11.25a.75.75 0 0 0-1.5 0v2.5h-2.5a.75.75 0 0 0 0 1.5h2.5v2.5a.75.75 0 0 0 1.5 0v-2.5h2.5a.75.75 0 0 0 0-1.5h-2.5v-2.5Z" clipRule="evenodd" />
                                    </svg>
                                    Add
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* MODIFIED: forcePage keeps the highlighted page in step with state, and the
                    pager is hidden when there is only one page. Moved inside the grid column
                    so it lines up with the cards rather than the whole page. */}
                {totalPages > 1 &&
                    <div className="flex justify-center pt-4">
                        <ReactPaginate
                            previousLabel={'\u2190'}
                            nextLabel={'\u2192'}
                            breakLabel={'...'}
                            pageCount={totalPages}
                            forcePage={currentPage - 1}
                            marginPagesDisplayed={1}
                            pageRangeDisplayed={2}
                            onPageChange={handlePageClick}
                            containerClassName={'flex flex-wrap justify-center gap-2 font-display font-semibold'}
                            pageClassName={'cv-btn cv-btn-quiet px-4 py-2 cursor-pointer'}
                            activeClassName={'!bg-brand-flame !text-white !border-brand-flame'}
                            previousClassName={'cv-btn cv-btn-outline px-4 py-2 cursor-pointer'}
                            nextClassName={'cv-btn cv-btn-outline px-4 py-2 cursor-pointer'}
                            disabledClassName={'opacity-50 cursor-not-allowed'}
                        />
                    </div>
                }
            </div>
        </section>

        <CarCard
            isVisible={isViewModalVisible}
            onClose={() => handleViewModalClick(null)}
            id={selectedCarId}
            onAddToCollection={(car) => { handleViewModalClick(null); setCarToAdd(car); }}
        />
        {/* MODIFIED: refresh the grid after a model is created, so it appears without a reload */}
        <NewCarForm
            isVisible={isCreateModalVisible}
            onClose={handleCreateButtonClick}
            /* MODIFIED: the dropdowns are refreshed alongside the grid, so a make or
               casting that did not exist before is immediately filterable. */
            onCreated={() => { fetchModels(); loadFilters(); }}
            // ADDED: when the model already exists, offer to shelve that one instead
            onAddExisting={(car) => setCarToAdd(car)}
        />
        {/* ADDED */}
        <CsvImportModal
            isVisible={isImportModalVisible}
            onClose={() => setIsImportModalVisible(false)}
            onImported={() => { fetchModels(); loadFilters(); }}
        />
        <AddToCollectionModal
            isVisible={Boolean(carToAdd)}
            onClose={() => setCarToAdd(null)}
            car={carToAdd}
        />
        </>
    );
}
